import React from 'react';
import { getOptimizedImageSources, getPublicWebpCandidate } from '../utils/optimizedImageUrls';

/**
 * App-standard responsive image: WebP via `<picture>` when available (Pexels or optional /public .webp).
 * Create React App has no built-in image optimizer; this is the single primitive to use for raster images.
 *
 * @param {object} props
 * @param {string} props.src — fallback `src` (e.g. JPEG/PNG URL)
 * @param {string} props.alt
 * @param {string} [props.className] — applied to the inner `<img>`
 * @param {'lazy'|'eager'} [props.loading]
 * @param {'async'|'auto'|'sync'} [props.decoding]
 * @param {'high'|'low'|'auto'} [props.fetchPriority]
 * @param {number} [props.width]
 * @param {number} [props.height]
 * @param {string} [props.sizes] — overrides default from CDN helper when set
 */
export default function OptimizedImage({
  src,
  alt,
  className,
  loading,
  decoding = 'async',
  fetchPriority,
  width,
  height,
  sizes,
}) {
  const pexels = getOptimizedImageSources(src);
  const publicWebp = pexels.webpAvailable ? null : getPublicWebpCandidate(src);

  const imgSrc = pexels.webpAvailable ? pexels.fallbackSrc : src;
  const resolvedSizes = sizes ?? pexels.sizes;

  return (
    <picture>
      {pexels.webpAvailable ? (
        <source type="image/webp" srcSet={pexels.webpSrcSet} sizes={resolvedSizes} />
      ) : null}
      {publicWebp ? <source type="image/webp" srcSet={publicWebp} /> : null}
      <img
        src={imgSrc}
        alt={alt || ''}
        className={className}
        loading={loading}
        decoding={decoding}
        fetchPriority={fetchPriority}
        width={width}
        height={height}
      />
    </picture>
  );
}
